import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, RefreshCcw, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, BASE, getToken } from '@/lib/api';

type WarehouseOption = { id: string; name: string };
type ProcessStatus = 'NEW' | 'READY' | 'PROCESSED' | 'FAILED';
type AttachmentRow = {
  id: string;
  warehouse_id?: string | null;
  document_type?: string | null;
  filename?: string | null;
  mime_type?: string | null;
  size_bytes?: number | null;
  notes?: string | null;
  process_status?: ProcessStatus | null;
  processed_entity_type?: string | null;
  processed_entity_id?: string | null;
  processed_entity_number?: string | null;
  process_note?: string | null;
  processed_at?: string | null;
  created_at?: string | null;
};
type LinkTarget = { id: string; number: string; secondary?: string | null; status?: string | null };

const DOCUMENT_TYPES = [
  'PO',
  'GRN',
  'QC_EVIDENCE',
  'DRAWING',
  'CERTIFICATE',
  'VENDOR_DOC',
  'CUSTOMER_DOC',
  'SITE_PHOTO',
  'OTHER',
] as const;

const FILE_STORE_REF_TYPE = 'WMS_FILE_STORE';
const FILE_STORE_REF_ID = '00000000-0000-4000-a000-00000000f001';

export default function WmsDocumentsPage() {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [warehouseId, setWarehouseId] = useState('NONE');
  const [documentType, setDocumentType] = useState<string>('OTHER');
  const [filterWarehouseId, setFilterWarehouseId] = useState<string>('ALL');
  const [filterDocumentType, setFilterDocumentType] = useState<string>('ALL');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<AttachmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [linkingItem, setLinkingItem] = useState<AttachmentRow | null>(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkTargets, setLinkTargets] = useState<LinkTarget[]>([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState('');

  const currentLinkKind = useMemo(() => {
    if (!linkingItem?.document_type) return null;
    if (linkingItem.document_type === 'GRN') return 'GRN';
    if (linkingItem.document_type === 'QC_EVIDENCE') return 'QC';
    return null;
  }, [linkingItem]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const rows = await api.get<WarehouseOption[]>('/wms/warehouses');
      setWarehouses(Array.isArray(rows) ? rows : []);
      setWarehouseId((prev) => prev || 'NONE');
      setFilterWarehouseId((prev) => prev || 'ALL');
    } catch {
      // keep warehouses as empty array — don't crash the page
    }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        referenceType: FILE_STORE_REF_TYPE,
        referenceId: FILE_STORE_REF_ID,
      });
      if (filterWarehouseId !== 'ALL') params.set('warehouseId', filterWarehouseId);
      if (filterDocumentType !== 'ALL') params.set('documentType', filterDocumentType);
      const res = await api.get<{ items?: AttachmentRow[] }>(`/wms/attachments?${params.toString()}`);
      setItems(res.items ?? []);
    } catch (err: any) {
      setItems([]);
      setError(err?.message ?? 'Failed to load WMS file store');
    } finally {
      setLoading(false);
    }
  }, [filterDocumentType, filterWarehouseId]);

  useEffect(() => {
    void fetchWarehouses();
  }, [fetchWarehouses]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!documentType) {
      setError('Document Type is required');
      return;
    }
    setUploading(true);
    setError('');
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('referenceType', FILE_STORE_REF_TYPE);
        formData.append('referenceId', FILE_STORE_REF_ID);
        formData.append('documentType', documentType);
        if (warehouseId !== 'NONE') formData.append('warehouseId', warehouseId);
        if (notes.trim()) formData.append('notes', notes.trim());
        const token = getToken();
        const response = await fetch(`${BASE}/wms/attachments`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
        if (!response.ok) {
          let message = `Upload failed (${response.status})`;
          try {
            const payload = await response.json();
            message = payload?.message ?? payload?.error ?? message;
          } catch {
            // ignore invalid json and keep fallback message
          }
          throw new Error(message);
        }
      }
      setNotes('');
      setDocumentType('OTHER');
      await fetchItems();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const loadLinkTargets = useCallback(async (item: AttachmentRow, search: string) => {
    setLinkLoading(true);
    setError('');
    try {
      if (item.document_type === 'GRN') {
        const data = await api.get<{ items: Array<Record<string, unknown>> }>(
          `/wms/inbound-orders?limit=20${search ? `&search=${encodeURIComponent(search)}` : ''}`,
        );
        setLinkTargets(
          (data.items ?? []).map((row: any) => ({
            id: String(row.id),
            number: String(row.asn_number ?? row.source_order_number ?? row.id),
            secondary: row.project_code ? String(row.project_code) : null,
            status: row.status ? String(row.status) : null,
          })),
        );
        return;
      }
      if (item.document_type === 'QC_EVIDENCE') {
        const data = await api.get<{ items?: Array<Record<string, unknown>> }>(
          `/wms/qc${search ? `?search=${encodeURIComponent(search)}` : ''}`,
        );
        setLinkTargets(
          (data.items ?? []).map((row: any) => ({
            id: String(row.id),
            number: String(row.qc_number ?? row.reference_number ?? row.id),
            secondary: row.reference_number ? String(row.reference_number) : null,
            status: row.status ? String(row.status) : null,
          })),
        );
        return;
      }
      setLinkTargets([]);
    } catch (err: any) {
      setLinkTargets([]);
      setError(err?.message ?? 'Failed to load processing targets');
    } finally {
      setLinkLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!linkingItem || !currentLinkKind) return;
    void loadLinkTargets(linkingItem, linkSearch.trim());
  }, [currentLinkKind, linkSearch, linkingItem, loadLinkTargets]);

  const openLinkSheet = (item: AttachmentRow) => {
    setLinkingItem(item);
    setLinkSearch('');
    setSelectedTargetId('');
    setLinkTargets([]);
  };

  const handleLinkSave = async () => {
    if (!linkingItem || !currentLinkKind || !selectedTargetId) return;
    const target = linkTargets.find((row) => row.id === selectedTargetId);
    if (!target) return;
    setLinkSaving(true);
    setError('');
    try {
      await api.patch(`/wms/attachments/${linkingItem.id}/process`, {
        processStatus: 'PROCESSED',
        processedEntityType: currentLinkKind === 'GRN' ? 'GRN' : 'QC_RECORD',
        processedEntityId: target.id,
        processedEntityNumber: target.number,
        processNote: currentLinkKind === 'GRN'
          ? 'Linked from Shared Documents to SRF / Inbound'
          : 'Linked from Shared Documents to QC record',
      });
      setLinkingItem(null);
      setSelectedTargetId('');
      setLinkTargets([]);
      await fetchItems();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save processing link');
    } finally {
      setLinkSaving(false);
    }
  };

  const renderStatus = (item: AttachmentRow) => {
    const status = item.process_status ?? 'NEW';
    const palette = status === 'PROCESSED'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'FAILED'
        ? 'bg-red-50 text-red-700 border-red-200'
        : status === 'READY'
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : 'bg-slate-50 text-slate-700 border-slate-200';
    return <span className={`inline-flex rounded border px-2 py-0.5 text-xs font-medium ${palette}`}>{status}</span>;
  };

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Shared Documents / WMS File Store</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-6">
            <div className="lg:col-span-2">
              <Label>Document Type</Label>
              <Select value={documentType} onValueChange={setDocumentType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
            <div className="lg:col-span-2">
              <Label>Warehouse</Label>
              <Select value={warehouseId} onValueChange={setWarehouseId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select warehouse (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <Label>Notes</Label>
              <Textarea className="mt-1" rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional file remarks" />
            </div>
            <div className="flex items-end gap-2 lg:col-span-6">
              <Label className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium hover:bg-muted">
                <Upload className="h-4 w-4" />
                {uploading ? 'Uploading...' : 'Upload'}
                <Input
                  type="file"
                  multiple
                  className="hidden"
                  disabled={uploading}
                  onChange={(event) => {
                    void handleUpload(event.target.files);
                    event.currentTarget.value = '';
                  }}
                />
              </Label>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-6">
            <div className="lg:col-span-2">
              <Label>Filter Document Type</Label>
              <Select value={filterDocumentType} onValueChange={setFilterDocumentType}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All document types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Document Types</SelectItem>
                  {DOCUMENT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-2">
              <Label>Filter Warehouse</Label>
              <Select value={filterWarehouseId} onValueChange={setFilterWarehouseId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All warehouses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Warehouses</SelectItem>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Use this area for PO files, QC evidence, vendor docs, drawings, and any Wasco warehouse supporting files.
            </p>
            <Button size="sm" variant="outline" onClick={() => void fetchItems()} disabled={loading}>
              <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {error ? (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="flex items-center gap-2 rounded border px-3 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading files...
            </div>
          ) : items.length === 0 ? (
            <div className="rounded border border-dashed px-3 py-8 text-center text-sm text-muted-foreground">
              No files uploaded yet
            </div>
          ) : (
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-left">File</th>
                    <th className="px-3 py-2 text-left">Document Type</th>
                    <th className="px-3 py-2 text-left">Warehouse</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Processed To</th>
                    <th className="px-3 py-2 text-left">MIME Type</th>
                    <th className="px-3 py-2 text-left">Size</th>
                    <th className="px-3 py-2 text-left">Notes</th>
                    <th className="px-3 py-2 text-left">Created</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="px-3 py-2 font-medium">{item.filename || '-'}</td>
                      <td className="px-3 py-2">{item.document_type || 'OTHER'}</td>
                      <td className="px-3 py-2">{warehouses.find((warehouse) => warehouse.id === item.warehouse_id)?.name || '-'}</td>
                      <td className="px-3 py-2">{renderStatus(item)}</td>
                      <td className="px-3 py-2">
                        {item.processed_entity_number ? (
                          <div className="space-y-1">
                            <div className="font-medium">{item.processed_entity_number}</div>
                            <div className="text-xs text-muted-foreground">
                              {item.processed_entity_type || '-'}
                              {item.processed_at ? ` · ${new Date(item.processed_at).toLocaleString()}` : ''}
                            </div>
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-3 py-2">{item.mime_type || '-'}</td>
                      <td className="px-3 py-2">{item.size_bytes?.toLocaleString() || '-'}</td>
                      <td className="px-3 py-2">{item.notes || '-'}</td>
                      <td className="px-3 py-2">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          {item.document_type === 'PO' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/wms/purchase-orders?openImport=1&importType=purchase-orders&attachmentId=${encodeURIComponent(item.id)}`)}
                            >
                              {item.process_status === 'PROCESSED' ? 'Reprocess PO' : 'Process PO'}
                            </Button>
                          )}
                          {item.document_type === 'GRN' && (
                            <Button size="sm" variant="outline" onClick={() => openLinkSheet(item)}>
                              {item.process_status === 'PROCESSED' ? 'Relink SRF' : 'Link SRF'}
                            </Button>
                          )}
                          {item.document_type === 'QC_EVIDENCE' && (
                            <Button size="sm" variant="outline" onClick={() => openLinkSheet(item)}>
                              {item.process_status === 'PROCESSED' ? 'Relink QC' : 'Link QC'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(linkingItem && currentLinkKind)} onOpenChange={(open) => !open && setLinkingItem(null)}>
        <DialogContent dock="right" className="flex max-h-[88vh] w-[min(100vw-32px,1000px)] max-w-none flex-col overflow-hidden p-0">
          <DialogHeader className="border-b px-5 py-4">
            <DialogTitle>{currentLinkKind === 'GRN' ? 'Link SRF Document' : 'Link QC Evidence'}</DialogTitle>
            <DialogDescription>
              {currentLinkKind === 'GRN'
                ? 'Select the inbound / SRF record this file belongs to.'
                : 'Select the QC record this evidence file belongs to.'}
            </DialogDescription>
          </DialogHeader>

          {linkingItem ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
              <div className="grid gap-4 rounded border p-4 lg:grid-cols-6">
                <div className="lg:col-span-3">
                  <Label>File</Label>
                  <div className="mt-1 text-sm font-medium">{linkingItem.filename || '-'}</div>
                </div>
                <div className="lg:col-span-3">
                  <Label>Document Type</Label>
                  <div className="mt-1 text-sm">{linkingItem.document_type || '-'}</div>
                </div>
                <div className="lg:col-span-6">
                  <Label>Search</Label>
                  <Input
                    className="mt-1"
                    value={linkSearch}
                    onChange={(event) => setLinkSearch(event.target.value)}
                    placeholder={currentLinkKind === 'GRN' ? 'Search SRF / Inbound No / Project' : 'Search QC No / Reference / SKU'}
                  />
                </div>
              </div>

              <div className="rounded border">
                <div className="border-b px-4 py-2 text-sm font-medium">
                  {currentLinkKind === 'GRN' ? 'Inbound / SRF Results' : 'QC Results'}
                </div>
                {linkLoading ? (
                  <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading records...
                  </div>
                ) : linkTargets.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-muted-foreground">
                    No matching records found
                  </div>
                ) : (
                  <div className="divide-y">
                    {linkTargets.map((target) => (
                      <label key={target.id} className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-muted/40">
                        <input
                          type="radio"
                          className="mt-1"
                          checked={selectedTargetId === target.id}
                          onChange={() => setSelectedTargetId(target.id)}
                        />
                        <div className="min-w-0">
                          <div className="font-medium">{target.number}</div>
                          <div className="text-sm text-muted-foreground">
                            {[target.secondary, target.status].filter(Boolean).join(' · ') || 'No extra details'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter className="border-t px-5 py-4">
            <Button variant="outline" onClick={() => setLinkingItem(null)} disabled={linkSaving}>Cancel</Button>
            <Button onClick={() => void handleLinkSave()} disabled={!selectedTargetId || linkSaving}>
              {linkSaving ? 'Saving...' : currentLinkKind === 'GRN' ? 'Link SRF' : 'Link QC'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
